import type { FieldErrors } from 'react-hook-form';

export interface FieldErrorEntry {
  field: string;
  message: string;
}

function toFieldLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/**
 * Collects every leaf error from a React Hook Form `FieldErrors` tree as
 * `{ field, message }` pairs, including nested records and array entries
 * such as `defrostSchedule[0].eventDays`.
 *
 * The `field` value is the immediate parent key converted to title-case
 * (e.g. `eventDays` → "Event Days").
 */
export function collectFieldErrors(errors: FieldErrors | undefined): FieldErrorEntry[] {
  const entries: FieldErrorEntry[] = [];

  const walk = (node: unknown, fieldLabel: string): void => {
    if (node == null || typeof node !== 'object') {
      return;
    }

    const record = node as Record<string, unknown>;
    const msg = record.message;
    if (typeof msg === 'string' && msg.trim().length > 0) {
      entries.push({ field: fieldLabel, message: msg });
    }

    if (Array.isArray(node)) {
      for (const item of node) {
        walk(item, fieldLabel);
      }
      return;
    }

    for (const [key, value] of Object.entries(record)) {
      if (key === 'ref') {
        continue;
      }
      if (value != null && typeof value === 'object') {
        walk(value, toFieldLabel(key));
      }
    }
  };

  walk(errors, '');
  return entries;
}
